import { Formik } from 'formik';
import { IAppointment } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist, useAppointmentDispatch, AppointmentDispatchTypes, useMaskVin } from '../../context/Appointment/appointment';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { CTAButton } from '../../controls/ButtonBase';
import { Heading } from '../../controls/Heading/Heading';
import { BackgroundDispatchTypes, useBackgroundDataDispatch } from '../../context/backgroundData';
import { nameof } from '../../helpers/nameof';
import { GMFormNumericField, GMFormVinField } from '../../controls/TextField';
import * as Yup from 'yup';
import { MileageProps } from './Mileage.types';
import { GMForm, GMFormWrapper } from '../../controls/Form';

export const Mileage = ({ onProceed, hideVin }: MileageProps) => {
    const Localization = useLocalization();
    const { formatRequired } = useLocalizationExtension();
    const appointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const maskVin = useMaskVin();
    const { mileage, vin } = appointment;
    const {
        dealer: { vinEnabled, vinRequired, blankInitialMileage },
    } = useKioskState();
    const initialValues = {
        mileage: mileage !== null ? (blankInitialMileage ? null : (mileage === 0 ? null : mileage)) : null,
        vin: vin,
    };
    const setAppointment = useAppointmentUpdateAndPersist();
    const backgroundDispatch = useBackgroundDataDispatch();

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('mileage')]: Yup.number()
                        .nullable()
                        .required(formatRequired(Localization.Mileage)),
                })}
            onSubmit={async (values) => {
                if (values.vin !== appointment.vin) {
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.UnmaskField,
                        payload: 'vin',
                    });
                }

                const updatedAppointment = {
                    ...appointment,
                    ...values,
                    mileageUpdated: initialValues.mileage !== values.mileage,
                } as IAppointment;

                setAppointment({
                    ...updatedAppointment,
                    ...values,
                }, undefined, undefined, true)
                    .then(() => {
                        backgroundDispatch({
                            type: BackgroundDispatchTypes.SetPastMileagePage,
                            payload: true,
                        });
                        onProceed();
                    });
            }}
        >
            {({ values }) => (
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={<GMAppointmentName />}
                            secondaryHeading={
                                hideVin
                                    ? Localization.EnterOdometerMileage
                                    : vinEnabled
                                        ? vinRequired
                                            ? Localization.EnterOdometerMileageAndVin
                                            : Localization.EnterOdometerMileageAndVinOptional
                                        : Localization.EnterOdometerMileage
                            }
                            leftAlign
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    }
                    topDivider
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <GMFormNumericField
                        required
                        name={nameof<typeof values>('mileage')}
                        id={nameof<typeof values>('mileage')}
                        title={Localization.Mileage}
                        label={Localization.Mileage}
                        ariaLabel={Localization.Mileage}
                        autoFocus={initialValues.mileage === null || initialValues.mileage === 0}
                    />
                    {vinEnabled && !hideVin && (
                        <GMFormVinField
                            name={nameof<typeof values>('vin')}
                            id={Localization.VIN}
                            title={Localization.VIN}
                            label={Localization.VIN}
                            ariaLabel={Localization.VIN}
                            vinType={'vin'}
                            required={vinRequired}
                            autoFocus={initialValues.mileage !== 0 && initialValues.mileage !== null && initialValues.vin === ''}
                            capsLock
                            initiallyMasked={maskVin}
                        />
                    )}
                </GMForm>
            )}
        </Formik>
    );
};
